import React from 'react';
import { SectionServices } from '../layout/SectionServices';
import { SectionNewDevelopment } from '../layout/SectionNewDevelopment';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { SectionInfoNarrow } from '../layout/SectionInfoNarrow';
import { Overlap } from '../layout/Overlap';
import { Page } from '../layout/Page';
import { Seo } from '../../utils/seo/Index';
import { StatusWrapper } from '../layout/StatusWrapper';
import { SectionNewsCard } from '../layout/SectionNewsCard';
import { ImageCarousel } from '../layout/carousel/ImageCarousel_v2';
import { Query, QueryGetPageContentInputArgs } from '../../../client/types/generated/types';
import { PageContentType } from '../../../server/global/enum';

//GraphQL
import { CONTENT_ITEM } from '../../../client/__graphql__/queries/page_content/getPageContent';
import styled from 'styled-components';
import { Paragraph } from '../ui/text/Paragraph';
import { RainbowLine } from '../ui/rainbowline/RainBowLine';

type GetPageItem = Pick<Query, 'getNdPageContent'>;
type GetPageItemVariables = QueryGetPageContentInputArgs;

const Office: React.FC = () => {
  const params = useParams<{ urltag: string }>();

  const { data, loading } = useQuery<GetPageItem, GetPageItemVariables>(CONTENT_ITEM, {
    fetchPolicy: 'network-only',
    variables: {
      input: { urltag: params.urltag, type: PageContentType.FRONTPAGE }
    }
  });

  if (loading) {
    return null;
  }

  // `PrivatMegleren - Nordeas Eiendomsmeglerkjede`

  return (
    <>
      <Seo
        title={`PrivatMegleren ${data?.getNdPageContent?.office?.name}`}
        image={data?.getNdPageContent?.seo?.image ? data?.getNdPageContent?.seo?.image : undefined}
        description={
          data?.getNdPageContent?.office?.name == 'Nyeboliger Bergen'
            ? 'Vi spesialiserer oss på salg av nye boliger i Bergen, og tilbyr personlig og kompetent service.'
            : data?.getNdPageContent?.seo?.text
            ? data?.getNdPageContent?.seo?.text
            : undefined
        }
      />
      <Page>
        <ImageCarousel
          url={data?.getNdPageContent?.image}
          imageList={data?.getNdPageContent?.imageList}
          title={data?.getNdPageContent?.title}
          get_properties={true}
        />
        <Overlap yPos={-70} yPos2={-20}>
          <SectionInfoNarrow info={data?.getNdPageContent?.text} hidedivider classNameInfo={'center'} />
        </Overlap>

        {data?.getNdPageContent?.config?.showStatistics ? <StatusWrapper /> : null}
        {data?.getNdPageContent?.config?.showFrontpageNews ? <SectionNewsCard /> : null}
        {data?.getNdPageContent?.config?.showFrontpageService ? <SectionServices style={{ marginTop: '2em' }} /> : null}
        <SectionNewDevelopment
          showProperties={data?.getNdPageContent?.config?.showSubpageProperties == true}
          showHighlighedProperty={data?.getNdPageContent?.config?.showSubpagePropertiesHighlight == true}
          showTitle
        />
      </Page>

      {params.urltag === 'nyeboligerbergen' ? (
        <>
          <>
            <AdvertisementOrOtherImportantCertifications>
              <RainbowLine className="line" height={'2px'} style={{ margin: ' 2em auto' }} />
              <Paragraph center={true}>Privatmegleren Nyeboliger Bergen er i 2024 blitt Miljøfyrtårn-sertifisert!</Paragraph>
              <Paragraph center={true}>
                Miljøfyrtårn er Norges mest brukte sertifikat for virksomheter som vil dokumentere sin miljøinnsats og vise samfunnsansvar, og er den
                første nasjonale ordningen i Europa som er blitt anerkjent av EU.
              </Paragraph>
              <Paragraph center={true}>
                Over 10 000 virksomheter er sertifisert som Miljøfyrtårn – fra små og mellomstore enkeltvirksomheter, til store konsern og kommuner.
                Miljøfyrtårn-sertifisering er anerkjent av myndighetene ved offentlige innkjøp, og godkjent som miljødokumentasjon i
                anbudskonkurranser.
              </Paragraph>

              <QualityStamp>
                <Logo src={'https://reeltimeprod-ahebbuatfggte7f2.z01.azurefd.net/privatmegleren/Miljfyrtarn-norsk-farger.jpg'} />
                <p>
                  Les mer om <a href="https://www.miljofyrtarn.no/">Miljøfyrtårn</a>{' '}
                </p>
              </QualityStamp>
            </AdvertisementOrOtherImportantCertifications>
          </>
        </>
      ) : params.urltag === 'nyeboligeroslo' ? (
        <>
          <>
            <AdvertisementOrOtherImportantCertifications>
              <RainbowLine className="line" height={'2px'} style={{ margin: ' 2em auto' }} />
              <Paragraph center={true}>Privatmegleren Nyeboliger Oslo er i 2024 blitt Miljøfyrtårn-sertifisert!</Paragraph>
              <Paragraph center={true}>
                Miljøfyrtårn er Norges mest brukte sertifikat for virksomheter som vil dokumentere sin miljøinnsats og vise samfunnsansvar, og er den
                første nasjonale ordningen i Europa som er blitt anerkjent av EU.
              </Paragraph>
              <Paragraph center={true}>
                Over 10 000 virksomheter er sertifisert som Miljøfyrtårn – fra små og mellomstore enkeltvirksomheter, til store konsern og kommuner.
                Miljøfyrtårn-sertifisering er anerkjent av myndighetene ved offentlige innkjøp, og godkjent som miljødokumentasjon i
                anbudskonkurranser.
              </Paragraph>

              <QualityStamp>
                <Logo src={'https://reeltimeprod-ahebbuatfggte7f2.z01.azurefd.net/privatmegleren/Miljfyrtarn-norsk-farger.jpg'} />
                <p>
                  Les mer om <a href="https://www.miljofyrtarn.no/">Miljøfyrtårn</a>{' '}
                </p>
              </QualityStamp>
            </AdvertisementOrOtherImportantCertifications>
          </>
        </>
      ) : null}
    </>
  );
};

const QualityStamp = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
export const Logo = styled.img`
  width: 100%;
  max-width: 150px;
  object-fit: contain;
  margin: 1.5em 0;
`;

const AdvertisementOrOtherImportantCertifications = styled.div`
  width: 100%;
  max-width: 36em;
  margin: 0 auto;
  padding: 0 2em;
  .line {
    width: 54%;
    @media all and (min-width: 600px) {
      width: 100%;
      padding: unset;
    }
  }
`;

export default Office;
